import { debounce } from 'lodash';
import { useEffect } from 'react';

import { DEFAULT_DEBOUNCE_TIME, SCROLL_DIRECTION } from 'constants/appConstants';

type ScrollDirection = (typeof SCROLL_DIRECTION)[keyof typeof SCROLL_DIRECTION];

const useScrollDirection = (
  containerSelector: string,
  direction: ScrollDirection,
  callback: () => void,
  delay = DEFAULT_DEBOUNCE_TIME
): void => {
  useEffect(() => {
    const container = document.querySelector(containerSelector);
    if (!container) {
      return;
    }

    let prevScrollTop = 0;
    const handleScroll = debounce((): void => {
      const currentContainer = container as HTMLElement;
      const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = currentContainer;

      const shouldTriggerCallback =
        (direction === SCROLL_DIRECTION.BOTTOM && scrollTop + clientHeight === scrollHeight) ||
        (direction === SCROLL_DIRECTION.TOP && scrollTop === 0) ||
        (direction === SCROLL_DIRECTION.LEFT && scrollLeft === 0) ||
        (direction === SCROLL_DIRECTION.RIGHT && scrollLeft + clientWidth === scrollWidth) ||
        (direction === SCROLL_DIRECTION.DOWN && prevScrollTop < scrollTop) ||
        (direction === SCROLL_DIRECTION.UP && prevScrollTop > scrollTop);

      if (shouldTriggerCallback) {
        callback();
      }

      prevScrollTop = scrollTop;
    }, delay);

    const onScroll = (): void => {
      handleScroll();
    };

    container.addEventListener('scroll', onScroll);
    return () => {
      container.removeEventListener('scroll', onScroll);
      handleScroll.cancel();
    };
  }, [callback, containerSelector, delay, direction]);
};

export default useScrollDirection;
